import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Table, Space, Tag, Typography, Modal, message, Breadcrumb } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { INIT_MALL_STRATEGIES, REGION_LABEL } from './mallMockData';
import MallAddStrategyDrawer from './components/MallAddStrategyDrawer';

const { Title, Text } = Typography;

// 兼容数组 / JSON 字符串
const parseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { const parsed = JSON.parse(val); return Array.isArray(parsed) ? parsed : []; } catch { return String(val).split(',').filter(Boolean); }
};

// ══════════════════════════════════════════════
//  商城位运营 - 策略列表
//  真实项目：views/operation/mall/index.vue
// ══════════════════════════════════════════════

const MallOperation = () => {
    const navigate = useNavigate();
    const [strategies, setStrategies] = useState(INIT_MALL_STRATEGIES);
    const [drawer, setDrawer] = useState({ open: false, editing: null });

    const handleSave = (values) => {
        if (drawer.editing) {
            setStrategies(prev => prev.map(s => (s.id === drawer.editing.id ? { ...s, ...values } : s)));
            message.success('编辑成功');
        } else {
            setStrategies(prev => [...prev, { id: Date.now(), isDefault: 0, packageCount: 0, ...values }]);
            message.success('添加成功');
        }
        setDrawer({ open: false, editing: null });
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '删除确认',
            content: `确定要删除策略"${record.name}"吗？`,
            okText: '确定', cancelText: '取消', okButtonProps: { danger: true },
            onOk: () => {
                setStrategies(prev => prev.filter(s => s.id !== record.id));
                message.success('删除成功');
            },
        });
    };

    const columns = [
        {
            title: '策略名称', key: 'name', width: 220,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{record.name}</div>
                    {record.isDefault === 1 && <div style={{ fontSize: 12, color: '#E6A23C' }}>未命中分群的用户展示此策略</div>}
                </div>
            ),
        },
        {
            title: '投放区域', key: 'releaseArea', width: 160,
            render: (_, record) => parseArray(record.releaseArea).length
                ? parseArray(record.releaseArea).map(area => <Tag key={area} color={area === '1' ? 'blue' : 'green'} style={{ marginRight: 4 }}>{REGION_LABEL[area] || area}</Tag>)
                : '-',
        },
        {
            title: '投放APP', key: 'releaseAppNames', width: 220,
            render: (_, record) => {
                const names = parseArray(record.releaseAppNames);
                return names.length ? names.map(app => <Tag key={app} color="green" style={{ marginBottom: 2 }}>{app}</Tag>) : '-';
            },
        },
        {
            title: '投放用户分群', key: 'segmentNames', width: 260,
            render: (_, record) => {
                if (record.isDefault === 1) return <span style={{ color: '#E6A23C' }}>——（默认策略不支持投放）</span>;
                const names = parseArray(record.segmentNames);
                return names.length ? names.map(seg => <Tag key={seg} style={{ marginBottom: 2 }}>{seg}</Tag>) : '-';
            },
        },
        {
            title: '套餐配置', key: 'packageCount', width: 130, align: 'center',
            render: (_, record) => (
                <Button type="link" size="small" style={{ padding: 0 }} icon={<EyeOutlined />} onClick={() => navigate(`/operation/mall/packageConfig?id=${record.id}`)}>
                    {record.packageCount || 0} 个套餐
                </Button>
            ),
        },
        { title: '备注', dataIndex: 'remark', key: 'remark', width: 200, ellipsis: true },
        {
            title: '操作', key: 'action', width: 150, align: 'center', fixed: 'right',
            render: (_, record) => (
                <Space size={8}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setDrawer({ open: true, editing: record })}>编辑</Button>
                    {record.isDefault !== 1 && (
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>删除</Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24, background: '#f5f6f8', minHeight: '100vh' }}>
            <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: '运营管理' }, { title: '商城位运营' }]} />
            <Title level={4} style={{ marginBottom: 16 }}>商城位运营</Title>

            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontSize: 16, fontWeight: 600 }}>商城位策略列表</span>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({ open: true, editing: null })}>添加策略</Button>
                </div>

                <Table
                    dataSource={strategies}
                    columns={columns}
                    rowKey="id"
                    bordered={false}
                    scroll={{ x: 1340 }}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                    locale={{ emptyText: <Text type="secondary">暂无策略</Text> }}
                />
            </Card>

            <MallAddStrategyDrawer
                open={drawer.open}
                editData={drawer.editing}
                onCancel={() => setDrawer({ open: false, editing: null })}
                onSuccess={handleSave}
            />
        </div>
    );
};

export default MallOperation;
